import { paperClasses } from '@mui/material/Paper';
import { alpha, Theme } from '@mui/material/styles';
import type { DataGridComponents } from '@mui/x-data-grid/themeAugmentation';
import { menuItemClasses } from '@mui/material/MenuItem';
import { listItemIconClasses } from '@mui/material/ListItemIcon';
import { iconButtonClasses } from '@mui/material/IconButton';
import { checkboxClasses } from '@mui/material/Checkbox';
import { listClasses } from '@mui/material/List';
import { gridClasses } from '@mui/x-data-grid';
import { tablePaginationClasses } from '@mui/material/TablePagination';
import { gray } from '../../../shared-theme/themePrimitives';

/* eslint-disable import/prefer-default-export */
export const dataGridCustomizations: DataGridComponents<Theme> = {
  MuiDataGrid: {
      defaultProps: {
        disableColumnMenu: true,
        disableColumnFilter: true,
        disableColumnSelector: true,
        disableColumnResize: true,
        hideFooter: true,
        density: "compact",
      },
    styleOverrides: {
      root: ({ theme }) => ({
        '--DataGrid-overlayHeight': '300px',
        overflow: 'clip',
        borderColor: (theme.vars || theme).palette.background.paper,
        backgroundColor: (theme.vars || theme).palette.background.default,
        // Set grid lines color to theme paper color
        [`& .${gridClasses.cell}`]: {
          borderColor: (theme.vars || theme).palette.background.paper,
        },
        [`& .${gridClasses.columnHeaders}`]: {
          borderColor: (theme.vars || theme).palette.background.paper,
        },
        [`& .${gridClasses.footerContainer}`]: {
          borderColor: (theme.vars || theme).palette.background.paper,
          borderBottom: `1px solid ${(theme.vars || theme).palette.background.paper} !important`,
        },
        [`& .${gridClasses.columnHeader}`]: {
          backgroundColor: (theme.vars || theme).palette.background.paper,
          textAlign: 'center',
          // Force consistent padding and margins regardless of device pixel ratio
          padding: '8px 8px !important', // Match default cell padding
          margin: '0 !important',
          minHeight: '56px !important',
          // Force consistent display properties
          display: 'flex !important',
          alignItems: 'center !important',
          justifyContent: 'center !important',
          // Override any device-specific CSS
          '& *': {
            margin: '0 !important',
            padding: '0 !important',
          },
        },
        // Force center alignment for column header titles
        [`& .${gridClasses.columnHeaderTitle}`]: {
          textAlign: 'center !important',
          justifyContent: 'center !important',
          alignItems: 'center !important',
          display: 'flex !important',
        },
        // Desktop-specific styling for column header titles
        [theme.breakpoints.up('md')]: {
          [`& .${gridClasses.columnHeaderTitle}`]: {
            fontSize: '1.0rem !important'
          },
        },
        [`& .${gridClasses.footerContainer}`]: {
          backgroundColor: (theme.vars || theme).palette.background.paper,
        },
        [`& .${checkboxClasses.root}`]: {
          padding: theme.spacing(0.5),
          '& > svg': {
            fontSize: '1rem',
          },
        },
        [`& .${tablePaginationClasses.root}`]: {
          marginRight: theme.spacing(1),
          '& .MuiIconButton-root': {
            maxHeight: 32,
            maxWidth: 32,
            '& > svg': {
              fontSize: '1rem',
            },
          },
        },
        // Mobile-specific overrides for xs and sm screens
        [theme.breakpoints.down('md')]: {
          // Hide right-side elements that might be causing alignment issues
          [`& .${gridClasses.iconButtonContainer}`]: {
            display: 'none !important',
          },
          [`& .${gridClasses.menuIcon}`]: {
            display: 'none !important',
          },
          [`& .${gridClasses.sortIcon}`]: {
            display: 'none !important',
          },
          // Reduce column header margins on mobile
          [`& .${gridClasses.columnHeader}`]: {
            padding: '4px 6px !important',
            margin: '0 !important',
          },
        }
      }),
      cell: ({ theme }) => ({ 
        borderTopColor: (theme.vars || theme).palette.divider,
        fontFamily: 'Roboto, Courier New, monospace', // Set fixed-width font 
        textAlign: 'center'
      }),
      menu: ({ theme }) => ({
        borderRadius: theme.shape.borderRadius,
        backgroundImage: 'none',
        [`& .${paperClasses.root}`]: {
          border: `1px solid ${(theme.vars || theme).palette.divider}`,
        },

        [`& .${menuItemClasses.root}`]: {
          margin: '0 4px',
        },
        [`& .${listItemIconClasses.root}`]: {
          marginRight: 0,
        },
        [`& .${listClasses.root}`]: {
          paddingLeft: 0,
          paddingRight: 0,
        },
      }),

      row: ({ theme }) => ({
        '&:last-of-type': { borderBottom: `1px solid ${(theme.vars || theme).palette.background.paper}` },
        '&:hover': {
          backgroundColor: (theme.vars || theme).palette.action.hover,
        },
        '&.Mui-selected': {
          background: (theme.vars || theme).palette.action.selected,
          '&:hover': {
            backgroundColor: (theme.vars || theme).palette.action.hover,
          },
        },
      }),
      iconButtonContainer: ({ theme }) => ({
        [`& .${iconButtonClasses.root}`]: {
          border: 'none',
          backgroundColor: 'transparent',
          '&:hover': {
            backgroundColor: alpha(theme.palette.action.selected, 0.3),
          },
          '&:active': {
            backgroundColor: gray[200],
          },
          ...theme.applyStyles('dark', {
            color: gray[50],
            '&:hover': {
              backgroundColor: gray[800],
            },
            '&:active': {
              backgroundColor: gray[900],
            },
          }),
        },
      }),
      menuIconButton: ({ theme }) => ({
        border: 'none',
        backgroundColor: 'transparent',
        '&:hover': {
          backgroundColor: gray[100],
        },
        '&:active': {
          backgroundColor: gray[200],
        },
        ...theme.applyStyles('dark', {
          color: gray[50],
          '&:hover': {
            backgroundColor: gray[800],
          },
          '&:active': {
            backgroundColor: gray[900],
          },
        }),
      }),
      filterForm: ({ theme }) => ({
        gap: theme.spacing(1),
        alignItems: 'flex-end',
      }),
      columnsManagementHeader: ({ theme }) => ({
        paddingRight: theme.spacing(3),
        paddingLeft: theme.spacing(3),
      }),
      columnHeaderTitleContainer: {
        flexGrow: 1,
        justifyContent: 'space-between',
        // Force consistent padding and margins regardless of device pixel ratio
        padding: '0 !important',
        margin: '0 !important',
        // Override any device-specific CSS
        '& *': {
          margin: '0 !important',
          padding: '0 !important',
        },
      },
      columnHeaderDraggableContainer: { 
        paddingRight: 2,
        // Force consistent padding and margins regardless of device pixel ratio
        padding: '0 8px 0 0 !important',
        margin: '0 !important',
        // Override any device-specific CSS
        '& *': {
          margin: '0 !important',
          padding: '0 !important',
        },
      },
    },
  },
};
